import React from 'react'
import { Nav } from '@scentregroup/shared/hub-components/universal-header/hamburger-menu/nav'
import { Text } from '@hub/text'
import { LinkBox, LinkOverlay } from '@hub/link'
import { LocationDropPinMediumIcon } from '@hub/icon'
import { Item } from '@scentregroup/shared/hub-components/universal-header/types'
import { sortBy } from 'lodash'

type Centre = {
  slug: string
  title: string
  state: string
}

type CentreSelectorNavProps = {
  country: 'Australia' | 'New Zealand'
  centres: Centre[]
  open: boolean
  onClose: () => void
}

type NavLinkProps = React.PropsWithChildren<{
  href: string
}>

export const CentreSelectorNav: React.FC<CentreSelectorNavProps> = ({
  centres,
  country,
  open,
  onClose,
}) => {
  const NavLink = React.useCallback<React.FC<NavLinkProps>>(
    props => (
      <LinkBox
        sx={{
          display: 'flex',
          gap: 'spacing-sm',
          alignItems: 'center',
          marginTop: 'spacing-md',
          marginBottom: 'spacing-md',
        }}
        onClick={onClose}
      >
        <LocationDropPinMediumIcon />
        <LinkOverlay {...props} />
      </LinkBox>
    ),
    [onClose]
  )

  const centresByState = React.useMemo(() => {
    const centresByStateUnsorted = centres
      .reduce<{ state: string; centres: Centre[] }[]>((acc, centre) => {
        const state = acc.find(({ state }) => centre.state === state)
        if (!state) {
          return acc.concat([{ state: centre.state, centres: [centre] }])
        }
        state.centres.push(centre)
        return acc
      }, [])
      .map(item => ({
        ...item,
        centres: sortBy(item.centres, 'title'),
      }))
    return sortBy(centresByStateUnsorted, 'state')
  }, [centres])

  const navItems = React.useMemo<Item[]>(() => {
    const headingItems: Item[] = [
      () => (
        <Text
          tone="strong"
          fontSize="font-xl"
          sx={{ marginBottom: 'spacing-md' }}
        >
          Find a store within a centre
        </Text>
      ),
      () => (
        <Text
          tone="strong"
          fontSize="font-md"
          sx={{ marginBottom: 'spacing-sm' }}
        >
          National
        </Text>
      ),
      () => <NavLink href="/stores">{country}</NavLink>,
    ]
    return headingItems.concat(
      centresByState.reduce((items, { state, centres }) => {
        return items.concat([
          () => <Text tone="strong">{state}</Text>,
          ...centres.map(centre => () => (
            <NavLink href={`/${centre.slug}/browse-stores`}>
              {centre.title}
            </NavLink>
          )),
        ])
      }, [] as Item[])
    )
  }, [NavLink, centresByState, country])

  return <Nav open={open} items={navItems} onClose={onClose} />
}
